import React, { memo, useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { CheckOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { useClickAway } from 'common/hooks';
import SpinLoading from 'common/components/spin-loading';

const SearchFilterComponent = memo(
  ({
    active,
    defaultOption,
    container,
    targetInput,
    className,
    isFetching,
    selectedValue: initialSelectedValue,
    options: propsOptions,
    optionLabelProp = 'name',
    optionValueProp,
    onChange,
    onClose,
    onSearch,
    onScrollEnd,
  }) => {
    const { t } = useTranslation();

    const searchComponentRef = useRef(null);
    const searchInputRef = useRef(null);

    const [showSearchList, setShowSearchList] = useState(active);
    const [options, setOptions] = useState([]);
    const [inputVal, setInputVal] = useState('');
    const [value, setValue] = useState([]);
    const [selectedValue, setSelectedValue] = useState([]);

    const debouncedOnSearch = useCallback(
      debounce(value => {
        onSearch?.(value);
      }, 500),
      [onSearch, value],
    );

    const handleInputChange = useCallback(e => {
      const value = e?.target?.value;
      setInputVal(value);
      debouncedOnSearch(value);
    }, []);

    const handleItemClick = ({ [optionValueProp]: itemValue }) => {
      if (selectedValue?.includes(itemValue)) {
        setSelectedValue(prevVal => prevVal.filter(item => item !== itemValue));
      } else {
        setSelectedValue(prevVal => [...prevVal, itemValue]);
      }
    };

    const handleClearClick = () => {
      if (!initialSelectedValue?.length) {
        return;
      }
      searchInputRef.current.value = null;

      setSelectedValue([]);
      setShowSearchList(false);
      setInputVal(null);
      setOptions(propsOptions);

      onChange([]);
      onClose();
    };

    const handleConfirmClick = () => {
      setValue(selectedValue);
      setShowSearchList(false);

      onChange(selectedValue);
      onClose();
    };

    const handleScroll = useCallback(
      debounce(e => {
        const preOffset = 10;
        if (e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight - preOffset) {
          onScrollEnd?.();
        }
      }, 500),
      [],
    );

    useEffect(() => {
      setValue(initialSelectedValue ?? []);
      setSelectedValue(initialSelectedValue ?? []);
    }, [initialSelectedValue]);

    useEffect(() => {
      setOptions(propsOptions);
    }, [optionLabelProp, propsOptions]);

    useEffect(() => {
      const handleFocus = () => {
        if (![...targetInput.classList].includes('disabled')) {
          setShowSearchList(prevVal => !prevVal);
        }
      };

      if (container && container.style) {
        container.style.position = 'relative';
      }

      if (targetInput) {
        targetInput.readOnly = true;
        targetInput.addEventListener('click', handleFocus);

        return () => targetInput.removeEventListener('click', handleFocus);
      }
    }, [container, targetInput]);

    useEffect(() => {
      if (!!defaultOption && !!propsOptions?.length && !isFetching) {
        let result = propsOptions?.filter(option => option?.slug === defaultOption);

        setOptions(result);
      }
    }, [!!defaultOption, propsOptions]);

    useClickAway(
      searchComponentRef,
      () => {
        setShowSearchList(false);
        setSelectedValue(value);
      },
      ['mousedown'],
    );

    useEffect(() => {
      if (showSearchList) {
        searchInputRef.current.focus();
      }
    }, [showSearchList]);

    useEffect(() => {
      setShowSearchList(active);
    }, [active]);

    if (!targetInput) {
      return null;
    }

    return (
      <div
        className={classNames('search-filter-component', className, {
          'search-filter-component--active': showSearchList,
        })}
        ref={searchComponentRef}
      >
        <div className="search-filter-component__input-container">
          <input
            className="search-filter-component__input"
            placeholder={t(
              'cms:cms.global.search_component.placeholder.please_input_search_content_here',
            )}
            value={inputVal}
            onChange={handleInputChange}
            ref={searchInputRef}
          />
          <SearchOutlined className="search-filter-component__search-icon" />
        </div>
        <Choose>
          <When condition={isFetching && !options?.length}>
            <div className="search-filter-component__loading-wrap">
              <LoadingOutlined style={{ fontSize: '24px' }} />
              <span className="search-filter-component__loading-text">
                {t('cms:cms.global.search_component.loading')}
              </span>
            </div>
          </When>
          <When condition={!options?.length}>
            <div className="search-filter-component__loading-wrap">
              <span className="search-filter-component__loading-text">
                {t('cms:cms.global.search_component.no_result')}
              </span>
            </div>
          </When>
          <Otherwise>
            <ul className="search-filter-component__results-list" onScroll={handleScroll}>
              <For of={options} each="option" index="index">
                <li
                  className={classNames('search-filter-component__results-item', {
                    'search-filter-component__results-item--checked': selectedValue?.includes(
                      option[optionValueProp],
                    ),
                  })}
                  key={index}
                  onClick={() => handleItemClick(option)}
                  role="presentation"
                  title={option[optionLabelProp]}
                >
                  {option[optionLabelProp]}
                  <If condition={selectedValue?.includes(option[optionValueProp])}>
                    <CheckOutlined className="search-filter-component__results-selected-icon" />
                  </If>
                </li>
              </For>
            </ul>
            {isFetching && <SpinLoading className="search-filter-component__loading" />}
            <div className="search-filter-component__operate-container">
              <span
                className={classNames(
                  'search-filter-component__btn',
                  'search-filter-component__btn--clear',
                  {
                    'search-filter-component__btn--clear--active': initialSelectedValue?.length > 0,
                  },
                )}
                onClick={handleClearClick}
              >
                {t('cms:cms.global.search_component.reset')}
              </span>

              <button
                type="button"
                className="search-filter-component__btn search-filter-component__btn--confirm"
                onClick={handleConfirmClick}
              >
                {t('cms:cms.global.search_component.ok')}
              </button>
            </div>
          </Otherwise>
        </Choose>
      </div>
    );
  },
);

SearchFilterComponent.propTypes = {
  targetInput: PropTypes.object,
  container: PropTypes.object,
  options: PropTypes.array.isRequired,
  selectedValue: PropTypes.array,
  className: PropTypes.string,
  optionLabelProp: PropTypes.string,
  optionValueProp: PropTypes.string,
  isFetching: PropTypes.bool,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
};

SearchFilterComponent.defaultProps = {
  targetInput: null,
  container: null,
  className: '',
  optionLabelProp: 'name',
  optionValueProp: '',
  selectedValue: [],
  isFetching: false,
  onChange: () => {},
  onClose: () => {},
};

SearchFilterComponent.displayName = 'SearchFilterComponent';

export default SearchFilterComponent;
